import { FileSignature } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TableSkeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProposalContractActions } from './proposal-contract-actions';
import type { ProposalContractViewModel } from './proposal-contract-types';

interface ProposalContractsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  contracts: ProposalContractViewModel[];
  isLoading?: boolean;
  error?: string | null;
  onView?: (contract: ProposalContractViewModel) => void;
  onDownload?: (contract: ProposalContractViewModel) => void;
}

export function ProposalContractsDialog({
  open,
  onOpenChange,
  title = 'Contrato',
  description = 'Contratos vinculados a esta proposta.',
  contracts,
  isLoading = false,
  error = null,
  onView,
  onDownload,
}: ProposalContractsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-gray-700" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <ProposalContractsDialogBody
          contracts={contracts}
          isLoading={isLoading}
          error={error}
          onView={onView}
          onDownload={onDownload}
        />
      </DialogContent>
    </Dialog>
  );
}

interface ProposalContractsDialogBodyProps {
  contracts: ProposalContractViewModel[];
  isLoading: boolean;
  error: string | null;
  onView?: (contract: ProposalContractViewModel) => void;
  onDownload?: (contract: ProposalContractViewModel) => void;
}

function ProposalContractsDialogBody({
  contracts,
  isLoading,
  error,
  onView,
  onDownload,
}: ProposalContractsDialogBodyProps) {
  if (isLoading) {
    return <TableSkeleton rows={3} />;
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (contracts.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhum contrato disponível para esta proposta.
      </p>
    );
  }

  return (
    <div className="max-h-[60vh] overflow-auto rounded-md border">
      <Table className="min-w-[520px]">
        <TableHeader>
          <TableRow>
            <TableHead>Contrato</TableHead>
            <TableHead className="w-[90px]">Versão</TableHead>
            <TableHead className="w-[160px] text-right">Ação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.map((contract) => (
            <TableRow key={contract.id}>
              <TableCell className="font-medium">{contract.name}</TableCell>
              <TableCell>v{contract.version}</TableCell>
              <TableCell>
                <ProposalContractActions
                  contract={contract}
                  onView={onView}
                  onDownload={onDownload}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
